import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry.ts";

// Prerendered JSON-LD exists for non-JS crawlers. Once React boots, the pages
// inject their own (richer) structured data via Helmet — keeping both would
// give rendering crawlers two AggregateRatings for the same item.
document
  .querySelectorAll('script[type="application/ld+json"][data-source="prerender"]')
  .forEach((node) => node.remove());

// Boot crash reporting without blocking first paint.
void initSentry();


createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);

// Helmet appends its own <meta name="description"> instead of replacing the
// prerendered one, which rendering crawlers (Bing) report as a duplicate tag.
// Helmet can write its copy at any point after hydration (and again on every
// route change), so watch <head> and always keep exactly one description —
// preferring the Helmet-managed tag over the prerendered/static fallback.
const dedupeHeadTags = () => {
  const dedupe = (selector: string) => {
    const all = Array.from(document.head.querySelectorAll(selector));
    if (all.length < 2) return;
    const keep = all.find((el) => el.hasAttribute("data-rh")) ?? all[all.length - 1];
    all.forEach((el) => {
      if (el !== keep) el.remove();
    });
  };
  dedupe('meta[name="description"]');
  dedupe('meta[property="og:description"]');
  dedupe('meta[name="twitter:description"]');
  dedupe('link[rel="canonical"]');
};

const observer = new MutationObserver(() => dedupeHeadTags());
observer.observe(document.head, { childList: true });
requestAnimationFrame(() => requestAnimationFrame(dedupeHeadTags));
